import { Platform } from 'react-native';
import * as Location from 'expo-location';
import { createPrefixedLogger } from './logger';

const log = createPrefixedLogger('location');

const DEFAULT_DESIRED_ACCURACY_M = 15;
const DEFAULT_TIMEOUT_MS = 60000;
// Threshold the UI hint copy treats as "accurate". Strict callers get nothing
// back when the timeout's best fix is worse than this; callers that pass
// `acceptCoarse` take it anyway and label it (see bestOnTimeout).
const ACCEPTABLE_ACCURACY_M = 25;

export interface AccurateFix {
  latitude: number;
  longitude: number;
  accuracyM: number | null;
}

/**
 * Get a GPS fix accurate enough to place a pin on the correct parcel.
 * Does NOT request permission — callers keep their existing
 * `requestForegroundPermissionsAsync()` gate before calling this.
 */
export async function getAccuratePosition(opts?: {
  desiredAccuracyM?: number;
  timeoutMs?: number;
  acceptCoarse?: boolean;
}): Promise<AccurateFix> {
  const desiredAccuracyM = opts?.desiredAccuracyM ?? DEFAULT_DESIRED_ACCURACY_M;
  const timeoutMs = opts?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const acceptCoarse = opts?.acceptCoarse ?? false;

  // expo-location's web shim only turns on GPS (enableHighAccuracy) when the
  // requested accuracy is above Balanced, and its watchPositionAsync path
  // never sets enableHighAccuracy at all regardless of what's asked for — so
  // on web we bypass the shim entirely and call the browser API directly
  // (same workaround as the locate-me handler in useUserLocation.web.ts).
  if (Platform.OS === 'web') {
    return getAccuratePositionWeb(desiredAccuracyM, timeoutMs, acceptCoarse);
  }
  return getAccuratePositionNative(desiredAccuracyM, timeoutMs, acceptCoarse);
}

/**
 * What to hand back when the timeout fires. Strict callers (the map's locate-me
 * dot) get nothing rather than a fix they'd have to caveat. Callers that pass
 * `acceptCoarse` take whatever the watch saw: outside cell range there's no
 * A-GPS, so accuracy converges slowly and a 40m fix — right street, wrong end
 * of it — beats the post having no location at all. The accuracy travels with
 * the fix so the caller can label it and let the user nudge the pin.
 */
function bestOnTimeout(best: AccurateFix | null, acceptCoarse: boolean): AccurateFix | null {
  if (!best) return null;
  if (acceptCoarse) return best;
  return best.accuracyM != null && best.accuracyM <= ACCEPTABLE_ACCURACY_M ? best : null;
}

function getAccuratePositionWeb(
  desiredAccuracyM: number,
  timeoutMs: number,
  acceptCoarse: boolean
): Promise<AccurateFix> {
  return new Promise((resolve, reject) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      reject(new Error('Geolocation is not available in this browser.'));
      return;
    }

    let best: AccurateFix | null = null;
    let settled = false;
    let watchId: number | null = null;

    // A leaked watch keeps the browser's GPS indicator (and hardware) on
    // after this resolves — every exit path below must clear it.
    const finish = (result: AccurateFix | null, error?: Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutHandle);
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      if (result) {
        log.debug('web fix, accuracyM:', result.accuracyM);
        resolve(result);
      } else {
        reject(error ?? new Error('No accurate location fix acquired.'));
      }
    };

    const timeoutHandle = setTimeout(() => {
      finish(bestOnTimeout(best, acceptCoarse));
    }, timeoutMs);

    watchId = navigator.geolocation.watchPosition(
      (position) => {
        const accuracyM = position.coords.accuracy ?? null;
        const fix: AccurateFix = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracyM,
        };
        // Null accuracy is worst-but-usable — only let it stand in for a
        // fix we don't have yet, never overwrite a fix with a known number.
        if (
          !best ||
          (accuracyM != null && (best.accuracyM == null || accuracyM < best.accuracyM))
        ) {
          best = fix;
        }
        if (accuracyM != null && accuracyM <= desiredAccuracyM) {
          finish(fix);
        }
      },
      (err) => {
        // Browsers emit transient POSITION_UNAVAILABLE/TIMEOUT errors
        // mid-watch (observed even on a simple accuracy change); only a
        // permission denial is final. Anything else: keep watching — the
        // outer timeout still bounds the wait.
        if (err.code === err.PERMISSION_DENIED) {
          finish(null, new Error(err.message || 'Location permission denied.'));
        }
      },
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 }
    );
  });
}

function getAccuratePositionNative(
  desiredAccuracyM: number,
  timeoutMs: number,
  acceptCoarse: boolean
): Promise<AccurateFix> {
  return new Promise((resolve, reject) => {
    let best: AccurateFix | null = null;
    let settled = false;
    let subscription: Location.LocationSubscription | null = null;

    // A leaked Highest-accuracy watch keeps GPS hardware hot after this
    // resolves — every exit path below must remove it.
    const finish = (result: AccurateFix | null, error?: Error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutHandle);
      subscription?.remove();
      if (result) {
        log.debug('native fix, accuracyM:', result.accuracyM);
        resolve(result);
      } else {
        reject(error ?? new Error('No accurate location fix acquired.'));
      }
    };

    const timeoutHandle = setTimeout(() => {
      finish(bestOnTimeout(best, acceptCoarse));
    }, timeoutMs);

    Location.watchPositionAsync(
      { accuracy: Location.Accuracy.Highest, timeInterval: 1000, distanceInterval: 0 },
      (loc) => {
        const accuracyM = loc.coords.accuracy ?? null;
        const fix: AccurateFix = {
          latitude: loc.coords.latitude,
          longitude: loc.coords.longitude,
          accuracyM,
        };
        // Null accuracy is worst-but-usable — only let it stand in for a
        // fix we don't have yet, never overwrite a fix with a known number.
        if (
          !best ||
          (accuracyM != null && (best.accuracyM == null || accuracyM < best.accuracyM))
        ) {
          best = fix;
        }
        if (accuracyM != null && accuracyM <= desiredAccuracyM) {
          finish(fix);
        }
      }
    )
      .then((sub) => {
        if (settled) {
          sub.remove();
        } else {
          subscription = sub;
        }
      })
      .catch((err) => finish(null, err instanceof Error ? err : new Error(String(err))));
  });
}

/**
 * True when a fix is loose enough that the pin may sit off the right parcel.
 * Callers that passed `acceptCoarse` use this to flag the hint, so an
 * approximate pin never passes for a precise one.
 */
export function isCoarseFix(accuracyM: number | null): boolean {
  return accuracyM == null || accuracyM > ACCEPTABLE_ACCURACY_M;
}

/** Meters to feet, rounded to the nearest 5 (min 5) — for hint copy like "~15 ft". */
export function metersToFeetLabel(m: number): string {
  const feet = Math.max(5, Math.round((m * 3.28084) / 5) * 5);
  return `${feet}`;
}
